/** Amministrazione → modello strutturato del programma di audit (AUD-F2.14b): sezioni e campi, ogni salvataggio è una nuova versione in uso. */
import { useEffect, useState } from "react";
import { Alert, Button, Card, Collapse, Input, Popconfirm, Select, Space, Tag, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { DEFAULT_PROGRAMME_SECTIONS, DEFAULT_PROGRAMME_TITLE, PROGRAMME_FIELD_TYPES, PROGRAMME_PREFILL, ProgrammeSections } from "@audit-plan/shared";
import type { ProgrammeFieldDef, ProgrammeModelDto, ProgrammeSectionDef } from "@audit-plan/shared";
import { activateProgrammeModel, saveProgrammeModel, useProgrammeModels } from "../../api/programme";
import { useNotify } from "../audit/useNotify";
import { fmtDT } from "../pills";
import { ResizableTable } from "../ResizableTable";

const MONO = { fontFamily: "var(--ap-mono)" } as const;
const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x)) as T;
const move = <T,>(a: T[], i: number, d: number): T[] => {
  const j = i + d;
  if (j < 0 || j >= a.length) return a;
  const c = [...a];
  [c[i], c[j]] = [c[j]!, c[i]!];
  return c;
};
const setAt = <T,>(a: T[], i: number, v: T): T[] => a.map((x, k) => (k === i ? v : x));
const delAt = <T,>(a: T[], i: number): T[] => a.filter((_, k) => k !== i);
const tx = (it = "", en = "") => ({ it, en });

function Arrows({ i, len, onMove, onDel, delLabel }: { i: number; len: number; onMove: (d: number) => void; onDel: () => void; delLabel: string }) {
  const { t } = useTranslation();
  return (
    <Space size={4}>
      <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => onMove(-1)} aria-label={t("pm_up")} />
      <Button size="small" icon={<ArrowDownOutlined />} disabled={i === len - 1} onClick={() => onMove(1)} aria-label={t("pm_down")} />
      <Popconfirm title={delLabel} okText={t("pm_delete")} cancelText={t("pm_cancel")} onConfirm={onDel}>
        <Button size="small" danger icon={<DeleteOutlined />} aria-label={t("pm_delete")} />
      </Popconfirm>
    </Space>
  );
}

/** Coppia di Input it/en per un testo bilingue. */
function Bi({ v, onChange, ph }: { v: { it: string; en: string }; onChange: (v: { it: string; en: string }) => void; ph: string }) {
  return (
    <>
      <Input style={{ flex: "1 1 180px", minWidth: 140 }} placeholder={`${ph} (IT)`} value={v.it} onChange={(e) => onChange({ ...v, it: e.target.value })} maxLength={500} />
      <Input style={{ flex: "1 1 180px", minWidth: 140 }} placeholder={`${ph} (EN)`} value={v.en} onChange={(e) => onChange({ ...v, en: e.target.value })} maxLength={500} />
    </>
  );
}

const row = { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" } as const;

function FieldEditor({ f, i, len, onChange, onMove, onDel }: { f: ProgrammeFieldDef; i: number; len: number; onChange: (f: ProgrammeFieldDef) => void; onMove: (d: number) => void; onDel: () => void }) {
  const { t } = useTranslation();
  const set = (p: Partial<ProgrammeFieldDef>) => onChange({ ...f, ...p });
  const opts = f.options ?? [];
  const cols = f.columns ?? [];
  const rows = f.rows ?? [];
  const setType = (type: ProgrammeFieldDef["type"]) => {
    const p: Partial<ProgrammeFieldDef> = { type };
    if ((type === "choice" || type === "multi") && !f.options?.length) p.options = [{ value: "", label: tx() }];
    if (type === "table" && !f.columns?.length) p.columns = [{ key: "", label: tx() }];
    set(p);
  };
  return (
    <div style={{ border: "1px solid var(--ant-color-border-secondary, #f0f0f0)", borderRadius: 6, padding: 10, marginBottom: 8 }}>
      <div style={row}>
        <Input style={{ width: 150, ...MONO }} placeholder={t("pm_key")} value={f.key} onChange={(e) => set({ key: e.target.value })} maxLength={40} />
        <Bi v={f.label} onChange={(label) => set({ label })} ph={t("pm_label")} />
        <Select style={{ width: 130 }} value={f.type} onChange={setType} options={PROGRAMME_FIELD_TYPES.map((x) => ({ value: x, label: t(`pm_type_${x}`) }))} />
        <Select
          style={{ width: 210 }}
          allowClear
          placeholder={t("pm_prefill")}
          value={f.prefill}
          onChange={(v) => set({ prefill: v })}
          options={PROGRAMME_PREFILL.map((x) => ({ value: x, label: t(`pm_pf_${x.replace(/\./g, "_")}`) }))}
        />
        <Arrows i={i} len={len} onMove={onMove} onDel={onDel} delLabel={t("pm_deleteFieldQ")} />
      </div>
      <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: "pointer", color: "var(--ant-color-text-secondary, #666)" }}>{t("pm_help")}</summary>
        <div style={{ ...row, marginTop: 6 }}>
          <Bi v={f.help ?? tx()} onChange={(h) => set({ help: h.it || h.en ? h : undefined })} ph={t("pm_help")} />
        </div>
      </details>
      {(f.type === "choice" || f.type === "multi") && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text strong>{t("pm_options")}</Typography.Text>
          {opts.map((o, k) => (
            <div key={k} style={{ ...row, marginTop: 6 }}>
              <Input style={{ width: 140, ...MONO }} placeholder={t("pm_value")} value={o.value} onChange={(e) => set({ options: setAt(opts, k, { ...o, value: e.target.value }) })} maxLength={60} />
              <Bi v={o.label} onChange={(label) => set({ options: setAt(opts, k, { ...o, label }) })} ph={t("pm_label")} />
              <Button size="small" danger icon={<DeleteOutlined />} aria-label={t("pm_delete")} onClick={() => set({ options: delAt(opts, k) })} />
            </div>
          ))}
          <Button size="small" style={{ marginTop: 6 }} icon={<PlusOutlined />} onClick={() => set({ options: [...opts, { value: "", label: tx() }] })}>{t("pm_addOption")}</Button>
        </div>
      )}
      {f.type === "table" && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text strong>{t("pm_columns")}</Typography.Text>
          {cols.map((c, k) => (
            <div key={k} style={{ ...row, marginTop: 6 }}>
              <Input style={{ width: 140, ...MONO }} placeholder={t("pm_key")} value={c.key} onChange={(e) => set({ columns: setAt(cols, k, { ...c, key: e.target.value }) })} maxLength={40} />
              <Bi v={c.label} onChange={(label) => set({ columns: setAt(cols, k, { ...c, label }) })} ph={t("pm_label")} />
              <Button size="small" danger icon={<DeleteOutlined />} aria-label={t("pm_delete")} onClick={() => set({ columns: delAt(cols, k) })} />
            </div>
          ))}
          <Button size="small" style={{ marginTop: 6 }} icon={<PlusOutlined />} onClick={() => set({ columns: [...cols, { key: "", label: tx() }] })}>{t("pm_addColumn")}</Button>
          {cols.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Typography.Text strong>{t("pm_rows")}</Typography.Text>
              <div style={{ overflowX: "auto" }}>
                {rows.map((r, k) => (
                  <div key={k} style={{ ...row, flexWrap: "nowrap", marginTop: 6 }}>
                    {cols.map((c) => (
                      <Input key={c.key + String(cols.indexOf(c))} style={{ width: 160, flex: "none" }} placeholder={c.label.it || c.key} value={r[c.key] ?? ""} onChange={(e) => set({ rows: setAt(rows, k, { ...r, [c.key]: e.target.value }) })} maxLength={2000} />
                    ))}
                    <Button size="small" danger icon={<DeleteOutlined />} aria-label={t("pm_delete")} onClick={() => set({ rows: delAt(rows, k) })} />
                  </div>
                ))}
              </div>
              <Button size="small" style={{ marginTop: 6 }} icon={<PlusOutlined />} onClick={() => set({ rows: [...rows, {}] })}>{t("pm_addRow")}</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ProgrammeModelEditor() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const n = useNotify();
  const q = useProgrammeModels();
  const models = q.data ?? [];
  const active = models.find((m) => m.active);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [sections, setSections] = useState<ProgrammeSectionDef[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reset = (m?: ProgrammeModelDto) => {
    setTitle(m?.title ?? DEFAULT_PROGRAMME_TITLE);
    setSections(clone(m?.sections ?? DEFAULT_PROGRAMME_SECTIONS));
    setNote("");
    setErrors([]);
  };
  useEffect(() => {
    if (q.data) reset(q.data.find((m) => m.active));
    // si ricarica la copia di lavoro solo quando cambia il modello in uso
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, q.isSuccess]);

  const setSec = (i: number, s: ProgrammeSectionDef) => setSections((a) => setAt(a, i, s));
  const refresh = () => void qc.invalidateQueries({ queryKey: ["programme-models"] });

  const save = async () => {
    const r = ProgrammeSections.safeParse(sections);
    if (!title.trim()) {
      setErrors([t("pm_titleRequired")]);
      return;
    }
    if (!r.success) {
      const msgs = r.error.issues.slice(0, 6).map((is) => `${is.path.length ? is.path.join(" › ") + ": " : ""}${is.message}`);
      setErrors(msgs);
      n.fail(new Error(msgs[0]));
      return;
    }
    setErrors([]);
    setBusy(true);
    try {
      await saveProgrammeModel({ title: title.trim(), sections: r.data, note: note.trim() });
      setNote("");
      refresh();
      n.ok();
    } catch (e) {
      n.fail(e);
    } finally {
      setBusy(false);
    }
  };
  const activate = async (id: string) => {
    setBusy(true);
    try {
      await activateProgrammeModel(id);
      refresh();
      n.ok();
    } catch (e) {
      n.fail(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("pm_title")} loading={q.isLoading}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t("pm_intro")} />
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input addonBefore={t("pm_modelTitle")} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        <Collapse
          items={sections.map((s, i) => ({
            key: i,
            label: (
              <span>
                <span style={MONO}>{s.key || "…"}</span> · {s.title.it || s.title.en || t("pm_untitled")} <Tag>{s.fields.length}</Tag>
              </span>
            ),
            extra: (
              <span onClick={(e) => e.stopPropagation()}>
                <Arrows i={i} len={sections.length} onMove={(d) => setSections((a) => move(a, i, d))} onDel={() => setSections((a) => delAt(a, i))} delLabel={t("pm_deleteSectionQ")} />
              </span>
            ),
            children: (
              <>
                <div style={{ ...row, marginBottom: 12 }}>
                  <Input style={{ width: 160, ...MONO }} placeholder={t("pm_key")} value={s.key} onChange={(e) => setSec(i, { ...s, key: e.target.value })} maxLength={40} />
                  <Bi v={s.title} onChange={(title) => setSec(i, { ...s, title })} ph={t("pm_sectionTitle")} />
                </div>
                {s.fields.map((f, k) => (
                  <FieldEditor
                    key={k}
                    f={f}
                    i={k}
                    len={s.fields.length}
                    onChange={(nf) => setSec(i, { ...s, fields: setAt(s.fields, k, nf) })}
                    onMove={(d) => setSec(i, { ...s, fields: move(s.fields, k, d) })}
                    onDel={() => setSec(i, { ...s, fields: delAt(s.fields, k) })}
                  />
                ))}
                <Button icon={<PlusOutlined />} onClick={() => setSec(i, { ...s, fields: [...s.fields, { key: "", label: tx(), type: "text" }] })}>{t("pm_addField")}</Button>
              </>
            ),
          }))}
        />
        <Button icon={<PlusOutlined />} onClick={() => setSections((a) => [...a, { key: "", title: tx(), fields: [{ key: "", label: tx(), type: "text" }] }])}>{t("pm_addSection")}</Button>
        {errors.length > 0 && <Alert type="error" showIcon message={t("pm_invalid")} description={<ul style={{ margin: 0, paddingLeft: 18 }}>{errors.map((m, k) => <li key={k}>{m}</li>)}</ul>} />}
        <Input addonBefore={t("pm_versionNote")} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("pm_versionNotePh")} maxLength={500} />
        <Space wrap>
          <Button type="primary" loading={busy} onClick={() => void save()}>{t("pm_save")}</Button>
          <Button disabled={busy} onClick={() => reset(active)}>{t("pm_discard")}</Button>
          <Popconfirm title={t("pm_restoreQ")} okText={t("pm_restore")} cancelText={t("pm_cancel")} onConfirm={() => { setTitle(DEFAULT_PROGRAMME_TITLE); setSections(clone(DEFAULT_PROGRAMME_SECTIONS)); setErrors([]); }}>
            <Button disabled={busy}>{t("pm_restore")}</Button>
          </Popconfirm>
        </Space>
        <ResizableTable<ProgrammeModelDto>
          rowKey="id"
          size="small"
          loading={q.isLoading}
          dataSource={models}
          pagination={false}
          scroll={{ x: "max-content" }}
          columns={[
            { title: t("pg_version"), dataIndex: "v", width: 90, render: (v: number) => `v${v}` },
            { title: t("pm_modelTitle"), dataIndex: "title" },
            { title: t("pg_note"), dataIndex: "note" },
            { title: t("pm_createdAt"), dataIndex: "createdAt", width: 160, render: (d: string) => fmtDT(d) },
            { title: "", dataIndex: "active", width: 110, render: (a: boolean) => (a ? <Tag color="green">{t("pg_active")}</Tag> : null) },
            { title: "", width: 120, render: (_: unknown, r) => (!r.active ? <Button size="small" disabled={busy} onClick={() => void activate(r.id)}>{t("pm_use")}</Button> : null) },
          ]}
        />
      </Space>
    </Card>
  );
}
